/**
 * Client-side Open Graph / Twitter Card helpers (no react-helmet).
 */

export const SITE_ORIGIN = "https://6amico.com";
export const DEFAULT_SEO_TITLE = "6amico · Il social network del territorio italiano";
export const DEFAULT_SEO_DESC =
  "6amico è un social network italiano dedicato alla scoperta e alla valorizzazione dei borghi, delle frazioni, degli eventi e dei prodotti tipici dei territori.";
export const DEFAULT_SEO_IMAGE = "https://6amico.com/logo512.png";

let activePostSeoId = null;

export function upsertMeta(attr, key, content) {
  if (typeof document === "undefined" || !content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function applySeo({ title, description, url, image = DEFAULT_SEO_IMAGE }) {
  if (typeof document === "undefined") return;
  const safeImage =
    !image || image === SITE_ORIGIN || image === `${SITE_ORIGIN}/`
      ? DEFAULT_SEO_IMAGE
      : image;

  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", safeImage);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", "6amico");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", safeImage);
}

export function resolveLaunchSeo(pathname, search) {
  const params = new URLSearchParams(search || "");
  const canonical = `${SITE_ORIGIN}${pathname}${search || ""}`;

  if (pathname === "/piazze" || pathname.startsWith("/piazze/")) {
    return {
      title: "Diario e Piazza Social · 6amico",
      description:
        "Il Diario di Bordo e la Piazza Social di 6amico: ricordi, timbri e post dalla community dei borghi italiani.",
      url: canonical,
      image: DEFAULT_SEO_IMAGE,
    };
  }

  if (
    pathname === "/esplora-la-mappa" ||
    pathname.startsWith("/esplora-la-mappa/") ||
    pathname === "/mappa-globale"
  ) {
    const mode = (params.get("mode") || "").toLowerCase();
    const macro = (params.get("macro") || "").toLowerCase();
    if (mode === "mare") {
      return {
        title: "Mappa del Mare · 6amico",
        description:
          "Esplora spiagge, costa e rotte marine del network 6amico sulla mappa dinamica del mare.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    if (macro === "montagna") {
      return {
        title: "Mappa della Montagna · 6amico",
        description:
          "Sentieri, vette e percorsi di montagna del network 6amico sulla mappa dinamica.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    if (macro === "terra" || mode === "terra") {
      return {
        title: "Mappa della Terra · 6amico",
        description:
          "Colline, borghi e percorsi dell’entroterra del network 6amico sulla mappa dinamica.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    return {
      title: "Esplora la Mappa · 6amico",
      description:
        "Naviga Mare, Terra e Montagna sul network territoriale 6amico — una sola mappa, tre esperienze.",
      url: canonical,
      image: DEFAULT_SEO_IMAGE,
    };
  }

  return {
    title: DEFAULT_SEO_TITLE,
    description: DEFAULT_SEO_DESC,
    url: `${SITE_ORIGIN}${pathname === "/" ? "/" : pathname}`,
    image: DEFAULT_SEO_IMAGE,
  };
}

export function setActivePostSeoId(id) {
  activePostSeoId = id ?? null;
}

export function getActivePostSeoId() {
  return activePostSeoId;
}

export function clearPostSeoIf(postId) {
  if (activePostSeoId == null || String(activePostSeoId) !== String(postId)) return;
  activePostSeoId = null;
  if (typeof window === "undefined") return;
  applySeo(resolveLaunchSeo(window.location.pathname, window.location.search));
}
